import { invokeFunction, invokePublicFunction } from "../api";
import { toApiRequestError } from "../errors";

export interface PatIntakeAnswers {
  has_allergies?: boolean | null;
  allergy_details?: string;
  current_medications?: string;
  takes_blood_thinner?: boolean | null;
  blood_thinner_details?: string;
  prior_anesthesia_issue?: boolean | null;
  anesthesia_issue_details?: string;
  has_implants?: boolean | null;
  implant_details?: string;
  health_changes?: string;
  callback_phone?: string;
}

export type PatAiSource = "model" | "heuristic_fallback";
export type PatClinicalRisk = "low" | "medium" | "high";
export type PatClinicalFlagSeverity = "low" | "medium" | "high";

export interface PatClinicalFlag {
  code:
    | "allergy_review"
    | "medication_reconciliation"
    | "blood_thinner_review"
    | "anesthesia_history_review"
    | "implant_review"
    | "health_change_review"
    | "callback_confirmation";
  severity: PatClinicalFlagSeverity;
  summary: string;
}

export interface PatAiAssessment {
  source: PatAiSource;
  clinical_risk: PatClinicalRisk;
  risk_confidence: number;
  flagged_items: PatClinicalFlag[];
  recommended_next_step: string;
  limitation_summary: string;
}

export type PatReviewStatus = "pending_review" | "approved" | "more_info_requested" | "escalated";

export interface PatReviewEvent {
  review_event_id: string;
  review_status: PatReviewStatus;
  note: string | null;
  actor_user_id: string | null;
  actor_role: string;
  created_at: string;
}

export interface PublicPatIntake {
  link_id: string;
  facility_name: string;
  survey_date: string;
  visit_type: string;
  expires_at: string;
  intake_status: "draft" | "submitted";
  completion_percent: number;
  autosaved_at: string | null;
  submitted_at: string | null;
  answers: PatIntakeAnswers;
}

export interface PatBoardVisit {
  visit_id: string;
  facility_id: string;
  facility_name: string;
  survey_date: string;
  visit_type: string;
  active_link: {
    link_id: string;
    expires_at: string;
    revoked_at: string | null;
    access_count: number;
    last_accessed_at: string | null;
  } | null;
  latest_intake: {
    intake_id: string;
    link_id: string;
    intake_status: "draft" | "submitted";
    completion_percent: number;
    ai_assessment: PatAiAssessment | null;
    ai_model_version: string | null;
    ai_prompt_hash: string | null;
    ai_input_hash: string | null;
    ai_output_hash: string | null;
    ai_generated_at: string | null;
    ai_generated_by: string | null;
    review_status: PatReviewStatus | null;
    review_note: string | null;
    review_requested_at: string | null;
    review_updated_at: string | null;
    review_updated_by: string | null;
    review_completed_at: string | null;
    review_events: PatReviewEvent[];
    autosaved_at: string | null;
    submitted_at: string | null;
    updated_at: string;
  } | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseAnswers(value: unknown): PatIntakeAnswers {
  if (!isRecord(value)) return {};
  return {
    has_allergies: typeof value.has_allergies === "boolean" ? value.has_allergies : null,
    allergy_details: typeof value.allergy_details === "string" ? value.allergy_details : "",
    current_medications: typeof value.current_medications === "string" ? value.current_medications : "",
    takes_blood_thinner: typeof value.takes_blood_thinner === "boolean" ? value.takes_blood_thinner : null,
    blood_thinner_details: typeof value.blood_thinner_details === "string" ? value.blood_thinner_details : "",
    prior_anesthesia_issue: typeof value.prior_anesthesia_issue === "boolean" ? value.prior_anesthesia_issue : null,
    anesthesia_issue_details: typeof value.anesthesia_issue_details === "string" ? value.anesthesia_issue_details : "",
    has_implants: typeof value.has_implants === "boolean" ? value.has_implants : null,
    implant_details: typeof value.implant_details === "string" ? value.implant_details : "",
    health_changes: typeof value.health_changes === "string" ? value.health_changes : "",
    callback_phone: typeof value.callback_phone === "string" ? value.callback_phone : "",
  };
}

function parsePatAiAssessment(value: unknown): PatAiAssessment | null {
  if (!isRecord(value)) return null;
  if (!["model", "heuristic_fallback"].includes(String(value.source))) return null;
  if (!["low", "medium", "high"].includes(String(value.clinical_risk))) return null;
  return {
    source: value.source as PatAiSource,
    clinical_risk: value.clinical_risk as PatClinicalRisk,
    risk_confidence: typeof value.risk_confidence === "number" ? value.risk_confidence : 0,
    flagged_items: Array.isArray(value.flagged_items)
      ? value.flagged_items
          .filter((entry): entry is Record<string, unknown> => isRecord(entry))
          .map((entry) => ({
            code: String(entry.code ?? "") as PatClinicalFlag["code"],
            severity: ["low", "medium", "high"].includes(String(entry.severity))
              ? entry.severity as PatClinicalFlagSeverity
              : "low",
            summary: typeof entry.summary === "string" ? entry.summary : "",
          }))
          .filter((entry) => entry.summary.length > 0)
      : [],
    recommended_next_step: typeof value.recommended_next_step === "string" ? value.recommended_next_step : "",
    limitation_summary: typeof value.limitation_summary === "string" ? value.limitation_summary : "",
  };
}

function parseReviewEvent(value: unknown): PatReviewEvent | null {
  if (!isRecord(value)) return null;
  if (typeof value.review_event_id !== "string") return null;
  if (!["pending_review", "approved", "more_info_requested", "escalated"].includes(String(value.review_status))) return null;
  return {
    review_event_id: value.review_event_id,
    review_status: value.review_status as PatReviewStatus,
    note: typeof value.note === "string" ? value.note : null,
    actor_user_id: typeof value.actor_user_id === "string" ? value.actor_user_id : null,
    actor_role: typeof value.actor_role === "string" ? value.actor_role : "",
    created_at: typeof value.created_at === "string" ? value.created_at : "",
  };
}

function parsePublicPatIntake(value: unknown): PublicPatIntake {
  if (!isRecord(value)) throw new Error("Malformed PAT intake response");
  if (
    typeof value.link_id !== "string" ||
    typeof value.facility_name !== "string" ||
    typeof value.survey_date !== "string" ||
    typeof value.visit_type !== "string" ||
    typeof value.expires_at !== "string" ||
    typeof value.intake_status !== "string" ||
    typeof value.completion_percent !== "number"
  ) {
    throw new Error("Malformed PAT intake response");
  }

  return {
    link_id: value.link_id,
    facility_name: value.facility_name,
    survey_date: value.survey_date,
    visit_type: value.visit_type,
    expires_at: value.expires_at,
    intake_status: value.intake_status as "draft" | "submitted",
    completion_percent: value.completion_percent,
    autosaved_at: typeof value.autosaved_at === "string" ? value.autosaved_at : null,
    submitted_at: typeof value.submitted_at === "string" ? value.submitted_at : null,
    answers: parseAnswers(value.answers),
  };
}

export async function getPublicPatIntake(token: string): Promise<PublicPatIntake> {
  const { data, error } = await invokePublicFunction<unknown>("pat-intake", {
    method: "GET",
    path: `/${token}`,
  });
  if (error) throw toApiRequestError(error, "Failed to load PAT intake");
  return parsePublicPatIntake(data);
}

export async function savePublicPatIntakeDraft(token: string, answers: PatIntakeAnswers): Promise<PublicPatIntake> {
  const { data, error } = await invokePublicFunction<unknown>("pat-intake", {
    method: "PUT",
    path: `/${token}`,
    body: { answers },
  });
  if (error) throw toApiRequestError(error, "Failed to save PAT intake draft");
  return parsePublicPatIntake(data);
}

export async function submitPublicPatIntake(token: string, answers: PatIntakeAnswers): Promise<PublicPatIntake> {
  const { data, error } = await invokePublicFunction<unknown>("pat-intake", {
    method: "POST",
    path: `/${token}/submit`,
    body: { answers },
  });
  if (error) throw toApiRequestError(error, "Failed to submit PAT intake");
  return parsePublicPatIntake(data);
}

export async function getPatBoard(facilityId?: string): Promise<PatBoardVisit[]> {
  const path = facilityId ? `/board?facility_id=${encodeURIComponent(facilityId)}` : "/board";
  const { data, error } = await invokeFunction<{ visits?: unknown }>("pat-intake", {
    method: "GET",
    path,
  });
  if (error) throw toApiRequestError(error, "Failed to load PAT board");
  if (!data || !Array.isArray(data.visits)) throw new Error("Malformed PAT board response");
  return data.visits.map((row) => {
    if (!isRecord(row)) throw new Error("Malformed PAT board visit");
    if (
      typeof row.visit_id !== "string" ||
      typeof row.facility_id !== "string" ||
      typeof row.facility_name !== "string" ||
      typeof row.survey_date !== "string" ||
      typeof row.visit_type !== "string"
    ) {
      throw new Error("Malformed PAT board visit");
    }
    return {
      visit_id: row.visit_id,
      facility_id: row.facility_id,
      facility_name: row.facility_name,
      survey_date: row.survey_date,
      visit_type: row.visit_type,
      active_link: isRecord(row.active_link)
        ? {
          link_id: typeof row.active_link.link_id === "string" ? row.active_link.link_id : "",
          expires_at: typeof row.active_link.expires_at === "string" ? row.active_link.expires_at : "",
          revoked_at: typeof row.active_link.revoked_at === "string" ? row.active_link.revoked_at : null,
          access_count: typeof row.active_link.access_count === "number" ? row.active_link.access_count : 0,
          last_accessed_at: typeof row.active_link.last_accessed_at === "string" ? row.active_link.last_accessed_at : null,
        }
        : null,
      latest_intake: isRecord(row.latest_intake)
        ? {
          intake_id: typeof row.latest_intake.intake_id === "string" ? row.latest_intake.intake_id : "",
          link_id: typeof row.latest_intake.link_id === "string" ? row.latest_intake.link_id : "",
          intake_status: row.latest_intake.intake_status === "submitted" ? "submitted" : "draft",
          completion_percent: typeof row.latest_intake.completion_percent === "number" ? row.latest_intake.completion_percent : 0,
          ai_assessment: parsePatAiAssessment(row.latest_intake.ai_assessment),
          ai_model_version: typeof row.latest_intake.ai_model_version === "string" ? row.latest_intake.ai_model_version : null,
          ai_prompt_hash: typeof row.latest_intake.ai_prompt_hash === "string" ? row.latest_intake.ai_prompt_hash : null,
          ai_input_hash: typeof row.latest_intake.ai_input_hash === "string" ? row.latest_intake.ai_input_hash : null,
          ai_output_hash: typeof row.latest_intake.ai_output_hash === "string" ? row.latest_intake.ai_output_hash : null,
          ai_generated_at: typeof row.latest_intake.ai_generated_at === "string" ? row.latest_intake.ai_generated_at : null,
          ai_generated_by: typeof row.latest_intake.ai_generated_by === "string" ? row.latest_intake.ai_generated_by : null,
          review_status: ["pending_review", "approved", "more_info_requested", "escalated"].includes(String(row.latest_intake.review_status))
            ? row.latest_intake.review_status as PatReviewStatus
            : null,
          review_note: typeof row.latest_intake.review_note === "string" ? row.latest_intake.review_note : null,
          review_requested_at: typeof row.latest_intake.review_requested_at === "string" ? row.latest_intake.review_requested_at : null,
          review_updated_at: typeof row.latest_intake.review_updated_at === "string" ? row.latest_intake.review_updated_at : null,
          review_updated_by: typeof row.latest_intake.review_updated_by === "string" ? row.latest_intake.review_updated_by : null,
          review_completed_at: typeof row.latest_intake.review_completed_at === "string" ? row.latest_intake.review_completed_at : null,
          review_events: Array.isArray(row.latest_intake.review_events)
            ? row.latest_intake.review_events.map(parseReviewEvent).filter((entry): entry is PatReviewEvent => entry !== null)
            : [],
          autosaved_at: typeof row.latest_intake.autosaved_at === "string" ? row.latest_intake.autosaved_at : null,
          submitted_at: typeof row.latest_intake.submitted_at === "string" ? row.latest_intake.submitted_at : null,
          updated_at: typeof row.latest_intake.updated_at === "string" ? row.latest_intake.updated_at : "",
        }
        : null,
    };
  });
}

export async function issuePatIntakeLink(visitId: string): Promise<{ link_id: string | null; token: string; path: string; expires_at: string }> {
  const { data, error } = await invokeFunction<unknown>("pat-intake", {
    method: "POST",
    path: "/issue",
    body: { visit_id: visitId },
  });
  if (error) throw toApiRequestError(error, "Failed to issue PAT intake link");
  if (!isRecord(data) || typeof data.token !== "string" || typeof data.path !== "string" || typeof data.expires_at !== "string") {
    throw new Error("Malformed PAT issue response");
  }
  return {
    link_id: typeof data.link_id === "string" ? data.link_id : null,
    token: data.token,
    path: data.path,
    expires_at: data.expires_at,
  };
}

export async function revokePatIntakeLink(linkId: string): Promise<void> {
  const { error } = await invokeFunction("pat-intake", {
    method: "POST",
    path: "/revoke",
    body: { link_id: linkId },
  });
  if (error) throw toApiRequestError(error, "Failed to revoke PAT intake link");
}

export async function generatePatAiTriage(visitId: string): Promise<PatBoardVisit["latest_intake"]> {
  const { data, error } = await invokeFunction<unknown>("pat-intake", {
    method: "POST",
    path: "/triage",
    body: { visit_id: visitId },
  });
  if (error) throw toApiRequestError(error, "Failed to generate PAT AI triage");
  if (!isRecord(data) || !isRecord(data.intake)) throw new Error("Malformed PAT triage response");
  const intake: NonNullable<PatBoardVisit["latest_intake"]> = {
    intake_id: typeof data.intake.intake_id === "string" ? data.intake.intake_id : "",
    link_id: typeof data.intake.link_id === "string" ? data.intake.link_id : "",
    intake_status: data.intake.intake_status === "submitted" ? "submitted" : "draft",
    completion_percent: typeof data.intake.completion_percent === "number" ? data.intake.completion_percent : 0,
    ai_assessment: parsePatAiAssessment(data.intake.ai_assessment),
    ai_model_version: typeof data.intake.ai_model_version === "string" ? data.intake.ai_model_version : null,
    ai_prompt_hash: typeof data.intake.ai_prompt_hash === "string" ? data.intake.ai_prompt_hash : null,
    ai_input_hash: typeof data.intake.ai_input_hash === "string" ? data.intake.ai_input_hash : null,
    ai_output_hash: typeof data.intake.ai_output_hash === "string" ? data.intake.ai_output_hash : null,
    ai_generated_at: typeof data.intake.ai_generated_at === "string" ? data.intake.ai_generated_at : null,
    ai_generated_by: typeof data.intake.ai_generated_by === "string" ? data.intake.ai_generated_by : null,
    review_status: ["pending_review", "approved", "more_info_requested", "escalated"].includes(String(data.intake.review_status))
      ? data.intake.review_status as PatReviewStatus
      : null,
    review_note: typeof data.intake.review_note === "string" ? data.intake.review_note : null,
    review_requested_at: typeof data.intake.review_requested_at === "string" ? data.intake.review_requested_at : null,
    review_updated_at: typeof data.intake.review_updated_at === "string" ? data.intake.review_updated_at : null,
    review_updated_by: typeof data.intake.review_updated_by === "string" ? data.intake.review_updated_by : null,
    review_completed_at: typeof data.intake.review_completed_at === "string" ? data.intake.review_completed_at : null,
    review_events: Array.isArray(data.intake.review_events)
      ? data.intake.review_events.map(parseReviewEvent).filter((entry): entry is PatReviewEvent => entry !== null)
      : [],
    autosaved_at: typeof data.intake.autosaved_at === "string" ? data.intake.autosaved_at : null,
    submitted_at: typeof data.intake.submitted_at === "string" ? data.intake.submitted_at : null,
    updated_at: typeof data.intake.updated_at === "string" ? data.intake.updated_at : "",
  };
  return intake;
}

export async function updatePatReviewAction(
  visitId: string,
  action: "approve" | "request_more_info" | "escalate",
  note?: string,
): Promise<PatBoardVisit["latest_intake"]> {
  const { data, error } = await invokeFunction<unknown>("pat-intake", {
    method: "POST",
    path: "/review-action",
    body: {
      visit_id: visitId,
      action,
      note,
    },
  });
  if (error) throw toApiRequestError(error, "Failed to update PAT review action");
  if (!isRecord(data) || !isRecord(data.intake)) throw new Error("Malformed PAT review response");
  const intake: NonNullable<PatBoardVisit["latest_intake"]> = {
    intake_id: typeof data.intake.intake_id === "string" ? data.intake.intake_id : "",
    link_id: typeof data.intake.link_id === "string" ? data.intake.link_id : "",
    intake_status: data.intake.intake_status === "submitted" ? "submitted" : "draft",
    completion_percent: typeof data.intake.completion_percent === "number" ? data.intake.completion_percent : 0,
    ai_assessment: parsePatAiAssessment(data.intake.ai_assessment),
    ai_model_version: typeof data.intake.ai_model_version === "string" ? data.intake.ai_model_version : null,
    ai_prompt_hash: typeof data.intake.ai_prompt_hash === "string" ? data.intake.ai_prompt_hash : null,
    ai_input_hash: typeof data.intake.ai_input_hash === "string" ? data.intake.ai_input_hash : null,
    ai_output_hash: typeof data.intake.ai_output_hash === "string" ? data.intake.ai_output_hash : null,
    ai_generated_at: typeof data.intake.ai_generated_at === "string" ? data.intake.ai_generated_at : null,
    ai_generated_by: typeof data.intake.ai_generated_by === "string" ? data.intake.ai_generated_by : null,
    review_status: ["pending_review", "approved", "more_info_requested", "escalated"].includes(String(data.intake.review_status))
      ? data.intake.review_status as PatReviewStatus
      : null,
    review_note: typeof data.intake.review_note === "string" ? data.intake.review_note : null,
    review_requested_at: typeof data.intake.review_requested_at === "string" ? data.intake.review_requested_at : null,
    review_updated_at: typeof data.intake.review_updated_at === "string" ? data.intake.review_updated_at : null,
    review_updated_by: typeof data.intake.review_updated_by === "string" ? data.intake.review_updated_by : null,
    review_completed_at: typeof data.intake.review_completed_at === "string" ? data.intake.review_completed_at : null,
    review_events: Array.isArray(data.intake.review_events)
      ? data.intake.review_events.map(parseReviewEvent).filter((entry): entry is PatReviewEvent => entry !== null)
      : [],
    autosaved_at: typeof data.intake.autosaved_at === "string" ? data.intake.autosaved_at : null,
    submitted_at: typeof data.intake.submitted_at === "string" ? data.intake.submitted_at : null,
    updated_at: typeof data.intake.updated_at === "string" ? data.intake.updated_at : "",
  };
  return intake;
}
